import { useState, type ReactNode } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark } from '@fortawesome/pro-solid-svg-icons';
import { CharlieSheet, CharlieSheetContent } from '@ver.id/libs.ui.charlie';
import { HostedSidebar } from '../components/hosted/sidebar';
import HostedTopBar from '../components/hosted/TopBar';
import HostedFooter from '../components/hosted/Footer';
import HostedDemoBanner from '../components/hosted/DemoBanner';
import HostedLanguageSelector from '../components/hosted/LanguageSelector';
import HostedScrollArea from '../components/hosted/ScrollArea';
import HostedClientLogo from '../components/hosted/ClientLogo';
import ErrorBoundary from '../components/shared/ErrorBoundary';
import { useLayoutData } from '../hooks/useLayoutContext';
import { useTranslation } from '../i18n/i18n';

type MobileLayoutProps = {
  children: ReactNode;
  /** Sticky footer rendered outside the scroll area */
  footer?: ReactNode;
  /** Custom header replacing HostedTopBar + sidebar sheet (e.g. flow start page) */
  header?: ReactNode;
  /** Additional className for the scrollable content area */
  contentClassName?: string;
};

/**
 * HostedMobileLayout — Mobile page shell for Ronan v2.
 *
 * Reads sidebar/layout data from LayoutContext.
 * Default: top bar + sidebar sheet + scrollable content + sticky footer + branding.
 * With `header` prop: custom header replaces HostedTopBar, no sidebar sheet.
 */
export default function HostedMobileLayout({
  children,
  header,
  footer,
  contentClassName,
}: MobileLayoutProps) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { t } = useTranslation();
  const {
    client,
    requestType,
    interactionConfig,
    requestedDataGroups,
    requestedDataLoading,
    demo,
  } = useLayoutData();

  return (
    <div className="flex flex-col h-dvh w-full font-inter">
      {demo && <HostedDemoBanner />}

      {header ? (
        header
      ) : (
        <>
          <HostedTopBar
            client={client}
            onOpenSidebar={() => setSidebarOpen(true)}
          />

          <CharlieSheet open={sidebarOpen} onOpenChange={setSidebarOpen}>
            <CharlieSheetContent
              side="left"
              hideClose
              className="w-full max-w-full bg-sidebar p-0"
            >
              <div className="flex items-center justify-between border-b border-border bg-background px-4 py-3">
                <div className="flex flex-1 items-start">
                  <HostedLanguageSelector compact />
                </div>
                <div className="flex items-center justify-center shrink-0">
                  <HostedClientLogo client={client} />
                </div>
                <div className="flex flex-1 items-start justify-end">
                  <button
                    type="button"
                    onClick={() => setSidebarOpen(false)}
                    className="flex items-center justify-center size-10 rounded-md border border-input bg-background"
                    aria-label={t('topBar.closeMenu')}
                  >
                    <FontAwesomeIcon icon={faXmark} className="size-4" />
                  </button>
                </div>
              </div>
              <HostedScrollArea className="px-4 py-6">
                <HostedSidebar
                  client={client}
                  requestType={requestType}
                  interactionConfig={interactionConfig}
                  requestedDataGroups={requestedDataGroups}
                  requestedDataLoading={requestedDataLoading}
                  variant="mobile"
                />
              </HostedScrollArea>
            </CharlieSheetContent>
          </CharlieSheet>
        </>
      )}

      <main
        className={`flex flex-1 flex-col overflow-y-auto min-h-0 ${contentClassName ?? 'items-center p-4'}`}
      >
        <ErrorBoundary>{children}</ErrorBoundary>
      </main>

      {footer && (
        <div className="shrink-0 border-t border-border bg-background px-4 pt-4 pb-2">
          {footer}
        </div>
      )}

      <HostedFooter />
    </div>
  );
}
